import { setViewModel } from './bookSlice';
import { setState as setAppState } from '../appState/appStateSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';
import moment from 'moment';
import { NavigateParams } from '../../components/BtNavigate';
import { RootState, store } from '../../app/store';
import { ButtonParams } from '../../components/BtButton';
import { InputParams } from '../../components/BtInput';
import {
  Company,
  NoCompany,
  NoPerson,
  NoVehicle,
  NoWeight,
  Person,
  Vehicle,
  Weight,
} from '../../components/BtGroup';
import { BtListViewParams } from '../../components/BtListView';
import {
  validateEmail,
  validateFirstName,
  validateLastName,
  validateMobile,
} from '../landing/validators';
import { Failure } from '../../support/apiCallHelper';
import { FailedParams } from '../../components/BtFailed';
import { SpinnerParams } from '../../components/BtSpinner';
import { LoadingParams } from '../../components/BtLoading';
import { createGatSelfServe, scheduleMissionByToken } from '../gat/gatApi';
import { setMissionData } from '../gat/gatSessionSlice';

export function none(logoUrl: string): BookScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      back: '',
      home: '',
      complete: '',
      change: '',
      yourInformation: '',
      phone: '',
      noMatch: '',
      weight: '',
      ok: '',
      thanks: '',
      body: '',
      bookingId: '',
      metaTitle: '',
      interrupt: '',
      loading: '',
      payment: '',
      email: '',
      contactPerson: '',
      firstName: '',
      lastName: '',
    },
    state: {
      phone: '',
      items: [null],
      vechicleItems: [null],
      customerItems: [null],
      id: '',
      email: '',
      firstName: '',
      lastName: '',
    },
    error: {
      status: '',
      body: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl,
      },
    },
  };
}

export namespace BookScreen {
  interface Texts {
    title: string;
    back: string;
    home: string;
    complete: string;
    change: string;
    yourInformation: string;
    phone: string;
    noMatch: string;
    weight: string;
    ok: string;
    thanks: string;
    body: string;
    bookingId: string;
    metaTitle: string;
    interrupt: string;
    loading: string;
    payment: string;
    email: string;
    contactPerson: string;
    firstName: string;
    lastName: string;
  }

  export interface State {
    phone: string;
    items: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ];
    vechicleItems: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ];
    customerItems: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ];
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  }

  export interface None {
    type: 'none';
    texts: Texts;
    state: State;
    error: FailedParams;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    greeting: string;
    change: ButtonParams;
    complete: ButtonParams;
    yourInformation: string;
    phone: InputParams;
    email: InputParams;
    firstName: InputParams;
    lastName: InputParams;
    vechicleItems: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ];
    customerItems: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ];
    date: BtListViewParams;
    time: BtListViewParams;
    from: BtListViewParams;
    arrowIcon: string;
    to: BtListViewParams;
    price: BtListViewParams;
    metaTitle: string;
    spinner: SpinnerParams;
    payment: string;
  }

  export interface Failed {
    type: 'failed';
    texts: Texts;
    state: State;
    error: FailedParams;
  }

  export interface Finish {
    type: 'finish';
    texts: Texts;
    state: State;
    navigation: NavigateParams;
    title: string;
    ok: ButtonParams;
    body: string;
    bookingId: string;
    id: string;
    image: string;
  }

  export type ViewModel = None | Loading | Ready | Failed | Finish;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }
  export function isFailed(viewModel: ViewModel): viewModel is Failed {
    return (viewModel as Failed).type === 'failed';
  }
  export function isFinish(viewModel: ViewModel): viewModel is Finish {
    return (viewModel as Finish).type === 'finish';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    if (isFailed(viewModel)) return viewModel.texts;
    if (isFinish(viewModel)) return viewModel.texts;
    return {
      title: '',
      back: '',
      home: '',
      complete: '',
      change: '',
      yourInformation: '',
      phone: '',
      noMatch: '',
      weight: '',
      ok: '',
      thanks: '',
      body: '',
      bookingId: '',
      metaTitle: '',
      interrupt: '',
      loading: '',
      payment: '',
      email: '',
      contactPerson: '',
      firstName: '',
      lastName: '',
    };
  }

  function loading(viewModel: ViewModel, state: State, logoUrl: string): Loading {
    const t = i18n.t;
    return {
      type: 'loading',
      texts: {
        title: t('bookScreen.title'),
        back: t('bookScreen.back'),
        home: t('bookScreen.home'),
        complete: t('bookScreen.complete'),
        change: t('bookScreen.change'),
        yourInformation: t('bookScreen.yourInformation'),
        phone: t('bookScreen.phone'),
        noMatch: t('finderScreen.noMatch'),
        weight: t('bookScreen.weight'),
        ok: t('bookScreen.ok'),
        thanks: t('bookScreen.thanks'),
        body: t('bookScreen.body'),
        bookingId: t('bookScreen.bookingId'),
        metaTitle: t('bookScreen.metaTitle'),
        interrupt: t('bookScreen.interrupt'),
        loading: t('loading.title'),
        payment: t('bookScreen.payment'),
        email: t('bookScreen.email'),
        contactPerson: t('bookScreen.contactPerson'),
        firstName: t('bookScreen.firstName'),
        lastName: t('bookScreen.lastName'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
      state: state,
    };
  }

  const formatIsoToDate = (isoString: string): string => {
    return isoString.split('T')[0];
  };

  function ready(
    viewModel: ViewModel,
    state: State,
    errorPhone: string | null,
    errorEmail: string | null,
    errorFirstName: string | null,
    errorLastName: string | null,
    logoUrl: string,
  ): Ready {
    const texts = getTexts(viewModel);
    const appState = store.getState().appState.state;

    return {
      type: 'ready',
      texts: texts,
      greeting: texts.title,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      state: state,
      change: { title: texts.change, image: null, visible: true },
      complete: { title: texts.complete, image: null, visible: true },
      yourInformation: viewModel.texts.yourInformation,
      phone: {
        id: 'phone',
        name: 'phone',
        title: texts.phone,
        message: errorPhone ? errorPhone : '',
        disabled: false,
        value: state.phone,
        placeholder: '',
        type: 'tel',
      },
      email: {
        id: 'email',
        name: 'email',
        title: texts.email,
        message: errorEmail ? errorEmail : '',
        disabled: false,
        value: state.email,
        placeholder: '',
        type: 'email',
      },
      firstName: {
        id: 'firstName',
        name: 'firstName',
        title: texts.firstName,
        message: errorFirstName ? errorFirstName : '',
        disabled: false,
        value: state.firstName,
        placeholder: '',
        type: 'text',
        visible: appState.isActive === 'company',
      },
      lastName: {
        id: 'lastName',
        name: 'lastName',
        title: texts.lastName,
        message: errorLastName ? errorLastName : '',
        disabled: false,
        value: state.lastName,
        placeholder: '',
        type: 'text',
        visible: appState.isActive === 'company',
      },
      vechicleItems: state.vechicleItems,
      customerItems: state.customerItems,
      date: {
        title: formatIsoToDate(appState.missionDate),
        image: 'ico_calendar_sm',
        visible: true,
      },
      time: { title: appState.missionTime, image: 'ico_time_sm', visible: true },
      from: { title: appState.sourceAddress, image: 'ico_position_sm', visible: true },
      arrowIcon: 'ico_route',
      to: { title: appState.destinationAddress, image: 'ico_position_sm', visible: true },
      price: { title: appState.missionPrice, image: 'ico_cost', visible: true, as: 'h4' },
      metaTitle: texts.metaTitle,
      spinner: { interrupt: { title: texts.interrupt, image: '', visible: true } },
      payment: texts.payment,
    };
  }

  function failed(
    viewModel: ViewModel,
    texts: Texts,
    state: State,
    body: string,
    status: string,
  ): Failed {
    return {
      type: 'failed',
      texts: texts,
      state: state,
      error: {
        status: status,
        body: body,
      },
    };
  }

  function finish(viewModel: ViewModel, texts: Texts, state: State, logoUrl: string): Finish {
    return {
      type: 'finish',
      texts: texts,
      state: state,
      title: texts.thanks,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      ok: { title: texts.ok, image: null, visible: true },
      body: texts.body,
      bookingId: texts.bookingId,
      id: state.id,
      image: 'ico_vehicle_transport_finished',
    };
  }

  export async function reset(): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none('')));
    };
  }

  export function start(
    viewModel: ViewModel,
    logoUrl: string,
  ): (dispatch: Dispatch<AnyAction>) => void {
    const t = i18n.t;
    const appState = store.getState().appState.state;
    if (!appState.sessionActive) {
      return (dispatch) => {
        dispatch(
          setViewModel(
            failed(viewModel, viewModel.texts, viewModel.state, t('failed.session'), ''),
          ),
        );
      };
    }

    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel, viewModel.state, logoUrl)));
    };
  }

  export async function load(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const itemList = buildItem(viewModel);

    const vechileItemsList: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ] = [null];
    const customertemsList: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ] = [null];

    itemList.forEach((item) => {
      if (
        item?.type !== 'vehicle' &&
        item?.type !== 'noVehicle' &&
        item?.type !== 'weight' &&
        item?.type !== 'noWeight' &&
        item !== null
      ) {
        customertemsList.push(item);
      } else if (item != null) {
        vechileItemsList.push(item);
      }
    });

    const newState: State = {
      ...viewModel.state,
      vechicleItems: vechileItemsList,
      customerItems: customertemsList,
      items: itemList,
    };

    return (dispatch) => {
      dispatch(setViewModel(ready(viewModel, newState, null, null, null, null, logoUrl)));
    };
  }

  export async function uppdateState(
    viewModel: ViewModel,
    event: React.ChangeEvent<HTMLInputElement>,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const target = event.target;
    const value = target.value;
    const newState = { ...viewModel.state, [target.name]: value };
    return async (dispatch) => {
      dispatch(await setViewModel(ready(viewModel, newState, null, null, null, null, logoUrl)));
    };
  }

  export async function book(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const appState = store.getState().appState.state;
    const errorPhone = validateMobile(viewModel.state.phone);
    const errorEmail = validateEmail(viewModel.state.email);
    const errorFirstName =
      appState.isActive === 'company' ? validateFirstName(viewModel.state.firstName) : null;
    const errorLastName =
      appState.isActive === 'company' ? validateLastName(viewModel.state.lastName) : null;

    if (errorPhone || errorEmail || errorFirstName || errorLastName) {
      return async (dispatch) => {
        await dispatch(
          setViewModel(
            ready(
              viewModel,
              viewModel.state,
              errorPhone,
              errorEmail,
              errorFirstName,
              errorLastName,
              logoUrl,
            ),
          ),
        );
      };
    } else {
      const gatSession = store.getState().gatSession;
      const mission = gatSession.mission;

      const updatedMissionData = {
        ...mission,
        missionDate: appState.missionDate,
        email: viewModel.state.email,
        phone: viewModel.state.phone,
        firstName: viewModel.state.firstName,
        lastName: viewModel.state.lastName,
      };

      try {
        await store.dispatch(createGatSelfServe(updatedMissionData));

        store.dispatch(
          setMissionData({
            ...mission,
            missionDate: appState.missionDate,
            email: viewModel.state.email,
            phone: viewModel.state.phone,
            firstName: viewModel.state.firstName,
            lastName: viewModel.state.lastName,
          }),
        );

        const result = await callScheduleMission();

        return async (dispatch) => {
          if (!(result instanceof Failure)) {
            const shortId = result;

            await dispatch(
              setViewModel(
                finish(
                  viewModel,
                  viewModel.texts,
                  {
                    ...viewModel.state,
                    id: shortId,
                  },
                  logoUrl,
                ),
              ),
            );

            await dispatch(
              setAppState({
                ...appState,
                id: shortId,
              }),
            );
          } else {
            const resp = result;

            if (resp.statusText === 'AbortError: The user aborted a request.') {
              dispatch(
                setViewModel(ready(viewModel, viewModel.state, null, null, null, null, logoUrl)),
              );
            } else {
              await dispatch(
                setViewModel(
                  failed(
                    viewModel,
                    viewModel.texts,
                    viewModel.state,
                    resp.status.toString(),
                    resp.statusText,
                  ),
                ),
              );
            }
          }
        };
      } catch (error) {
        return async (dispatch) => {
          await dispatch(
            setViewModel(
              failed(
                viewModel,
                viewModel.texts,
                viewModel.state,
                '500',
                'Ett tekniskt fel uppstod',
              ),
            ),
          );
        };
      }
    }
  }

  let controller: AbortController | null = null;
  export async function cancelFetch() {
    if (controller) {
      controller.abort();
    }
  }

  type ScheduleMissionResponse = {
    shortId: string;
  };

  export const formatMissionDateWithOffset = (isoStr: string): string => {
    const cleaned = isoStr.endsWith('Z') ? isoStr.slice(0, -1) : isoStr;
    return moment(cleaned).format();
  };

  export async function callScheduleMission(): Promise<string | Failure> {
    controller = new AbortController();
    const { missionId, token, mission } = store.getState().gatSession;

    const missionDate = mission?.missionDate;

    const formatedDate = formatMissionDateWithOffset(missionDate);

    if (!missionId || !token || !missionDate) {
      return new Failure(400, 'Missing mission data');
    }

    try {
      const result = await store
        .dispatch(
          scheduleMissionByToken({
            missionId,
            token,
            missionDate: formatedDate,
            controller,
          }),
        )
        .unwrap();

      const typedResult = result as ScheduleMissionResponse;

      return typedResult.shortId;
    } catch (e: any) {
      console.error('Error scheduling mission:', e);
      return new Failure(0, e.toString());
    }
  }

  function buildItem(
    viewModel: ViewModel,
  ): [Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null] {
    const appState = store.getState().appState.state;
    const newState: [
      Vehicle | NoVehicle | Weight | NoWeight | Person | NoPerson | Company | NoCompany | null,
    ] = [null];

    if (appState.registered) {
      if (appState.regno !== '' && appState.weight === '' && appState.vehicleType) {
        const vehicle: Vehicle = {
          type: 'vehicle',
          icon: 'ico_vehicle_info',
          model: appState.vehicleType,
          regno: appState.regno,
        };
        newState.push(vehicle);
      } else {
        const noVehicle: NoVehicle = {
          type: 'noVehicle',
          icon: 'ico_vehicle_no_info',
          message: viewModel.texts.noMatch,
        };
        newState.push(noVehicle);
      }
    }

    if (!appState.registered) {
      if (appState.weight.length > 0) {
        const weight: Weight = {
          type: 'weight',
          icon: 'ico_vehicle_info_weight',
          weight: `${appState.weight} ${viewModel.texts.weight}`,
        };
        newState.push(weight);
      } else {
        const noWeight: NoWeight = {
          type: 'noWeight',
          icon: 'ico_vehicle_info_weight',
          message: viewModel.texts.noMatch,
        };
        newState.push(noWeight);
      }
    }

    if (appState.isActive === 'person') {
      if (appState.govId !== '' && appState.customerName !== '') {
        const person: Person = {
          type: 'person',
          icon: 'ico_person_info',
          firstName: appState.customerName,
          lastName: '',
          street: '',
          zipCode: '',
          city: '',
        };
        newState.push(person);
      } else {
        const noCompany: NoPerson = {
          type: 'noPerson',
          icon: 'ico_person_no_info',
          message: viewModel.texts.noMatch,
        };
        newState.push(noCompany);
      }
    }

    if (appState.isActive === 'company') {
      if (appState.govNo !== '' && appState.customerName !== '') {
        const company: Company = {
          type: 'company',
          icon: 'ico_company_info',
          name: appState.customerName,
          street: '',
          zipCode: '',
          city: '',
        };
        newState.push(company);
      } else {
        const noCompany: NoCompany = {
          type: 'noCompany',
          icon: 'ico_company_no_info',
          message: viewModel.texts.noMatch,
        };
        newState.push(noCompany);
      }
    }

    return newState;
  }
}
